// client/src/socket/usePresenceSocket.js
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import sockets from './socket';
import { setOnlineUser } from '../redux/reducer/onlineUsers/action/action';
import { getOnlineUserViaRouterFunc } from '../redux/reducer/getOnlineUserviaRouter/action/action';

function normalizeId(value) {
  if (value === undefined || value === null) return '';

  if (typeof value === 'object') {
    return String(value?._id || value?.$oid || value?.id || value?.value || value?.toString?.() || '').trim();
  }

  return String(value).trim();
}

function getIdentityIds(user) {
  if (!user) return [];

  if (typeof user === 'string' || typeof user === 'number') {
    return [normalizeId(user)].filter(Boolean);
  }

  if (Array.isArray(user)) {
    return Array.from(new Set(user.flatMap(getIdentityIds).filter(Boolean)));
  }

  if (typeof user === 'object') {
    return Array.from(
      new Set(
        [
          user._id,
          user.workerID,
          user.workerId,
          user.userID,
          user.userId,
          user.userid,
          user.id,
          user.myid,
          user.myId,
          user.value,
          user.adminID,
          user.adminId,
          user.ownerID,
          user.ownerId,
          user.creatorID,
          user.creatorId,
        ]
          .map(normalizeId)
          .filter(Boolean)
      )
    );
  }

  return [];
}

function buildIdentity(user) {
  const ids = getIdentityIds(user);

  return {
    _id: normalizeId(user?._id) || ids[0] || '',
    workerID: normalizeId(user?.workerID || user?.workerId),
    workerId: normalizeId(user?.workerId || user?.workerID),
    userID: normalizeId(user?.userID || user?.userId || user?.userid),
    userId: normalizeId(user?.userId || user?.userID || user?.userid),
    userid: normalizeId(user?.userid || user?.userID || user?.userId),
    id: normalizeId(user?.id),
    myid: normalizeId(user?.myid),
    myId: normalizeId(user?.myId),
    value: normalizeId(user?.value),
    adminID: normalizeId(user?.adminID || user?.adminId),
    adminId: normalizeId(user?.adminId || user?.adminID),
    ownerID: normalizeId(user?.ownerID || user?.ownerId),
    ownerId: normalizeId(user?.ownerId || user?.ownerID),
    creatorID: normalizeId(user?.creatorID || user?.creatorId),
    creatorId: normalizeId(user?.creatorId || user?.creatorID),
    ids,
  };
}

function readOnlineIds(payload) {
  if (Array.isArray(payload)) return payload.map(normalizeId).filter(Boolean);
  if (Array.isArray(payload?.onlineUserIds)) {
    return payload.onlineUserIds.map(normalizeId).filter(Boolean);
  }
  return [];
}

function sendOfflineBeacon(identity) {
  try {
    if (!navigator.sendBeacon) return;

    const blob = new Blob(
      [JSON.stringify({ userId: identity?._id || identity?.ids?.[0] || '' })],
      { type: 'application/json' }
    );

    navigator.sendBeacon('/presence/offline/api', blob);
  } catch (error) {
    // При закрытии страницы ошибки игнорируем.
  }
}

export function emitPresenceLogout(user) {
  const identity = buildIdentity(user);
  if (!identity.ids.length) return;

  try {
    sockets.emit('presence:logout', { identity });
    sockets.emit('logout', identity); // совместимость со старым серверным кодом
  } catch (error) {
    // ignore
  }
}

export default function usePresenceSocket({ user, companyId } = {}) {
  const dispatch = useDispatch();
  const closeRef = useRef(false);

  const identity = useMemo(() => buildIdentity(user), [user]);
  const identityKey = identity.ids.join('|');
  const companyKey = normalizeId(companyId);

  useEffect(() => {
    if (!identity.ids.length) return undefined;

    const joinPresence = () => {
      closeRef.current = false;

      try {
        if (companyKey) sockets.emit('joinRoom', companyKey);

        sockets.emit('presence:join', {
          identity,
          companyId: companyKey || null,
        });

        // Совместимость со старым сервером.
        sockets.emit('pushmyid', identity);
        sockets.emit('presence:get');
      } catch (error) {
        console.error('presence join error:', error);
      }
    };

    const handlePresenceUpdate = (payload) => {
      const ids = readOnlineIds(payload);
      dispatch(setOnlineUser(ids));
      dispatch(getOnlineUserViaRouterFunc());
    };

    const handleConnect = () => joinPresence();

    const handleVisibleAgain = () => {
      if (document.visibilityState === 'visible') joinPresence();
    };

    const handlePageShow = () => joinPresence();

    const handlePageHide = (event) => {
      if (event?.persisted) return;
      if (closeRef.current) return;

      closeRef.current = true;

      try {
        sockets.emit('presence:leave', { reason: 'pagehide' });
        sockets.emit('updateForOnlineUsersStatus');
      } catch (error) {}

      sendOfflineBeacon(identity);
    };

    const pingTimer = window.setInterval(() => {
      try {
        sockets.emit('presence:ping', {
          identity,
          companyId: companyKey || null,
        });
      } catch (error) {}
    }, 10000);

    sockets.on('connect', handleConnect);
    sockets.on('presence:update', handlePresenceUpdate);
    sockets.on('online', handlePresenceUpdate);

    document.addEventListener('visibilitychange', handleVisibleAgain);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);

    joinPresence();

    return () => {
      window.clearInterval(pingTimer);

      sockets.off('connect', handleConnect);
      sockets.off('presence:update', handlePresenceUpdate);
      sockets.off('online', handlePresenceUpdate);

      document.removeEventListener('visibilitychange', handleVisibleAgain);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [dispatch, identityKey, companyKey]);
}
